/* eslint-disable react/no-danger */
import { Heading } from "@twilio-paste/core/heading";
import { Separator } from "@twilio-paste/core/separator";
import { graphql, useStaticQuery } from "gatsby";
import { HeaderNode, SitesQuery } from "queries";
import React from "react";
import { scrollSpy } from "react-scroll";
import { HeadingData } from "shared/components/TableOfContents/types";
import { Member, Property } from "types";
import { v4 as uuid } from "uuid";
import { addPrefixAndUpdateInternalLinks, getMethodSignature } from "../helper";
import { TableOfContents } from "../shared/components/TableOfContents";
import { PrimaryLayout } from "../shared/layouts";
import { slugify } from "../utils/stringUtils";
import { ClassMembers } from "./components/ClassMembers";
import { Container } from "./components/Container";
import { ContentContainer } from "./components/ContentContainer";
import { HeadingContainer } from "./components/HeadingContainer";
import { RightContainer } from "./components/RightContainer";
import { SeparatorContainer } from "./components/SeparatorContainer";

const generateHeadings = (parentName: string, nodes: [HeaderNode, ...Member[]]) => {
    const headings: HeadingData[] = [];
    const [firstNode, ...restNodes] = nodes;
    if (firstNode.methods && firstNode.methods.length) {
        headings.push({
            name: "Methods",
            slug: "",
            depth: 2
        });
        firstNode.methods.forEach((method: { name: string; params: Property[]; returns: Property[] }) => {
            headings.push({
                name: method.name,
                slug: slugify(getMethodSignature(method.name, method.params, method.returns)),
                depth: 3
            });
        });
    }

    if (restNodes.length) {
        headings.push({
            name: "Members",
            slug: "",
            depth: 2
        });
        restNodes.forEach((node) => {
            headings.push({
                name: node.name,
                slug: node.name,
                depth: 3
            });
        });
    }

    return headings;
};
const query = graphql`
    {
        pages: allSitePage {
            nodes {
                ...SitePath
            }
        }
    }
`;

const DynamicPage: React.FC<{
    name: string;
    description?: string;
    nodes: [HeaderNode, ...Member[]];
}> = ({ name, description, nodes }) => {
    const headings = generateHeadings(name, nodes);
    const { pages }: SitesQuery = useStaticQuery(query);
    description = description && addPrefixAndUpdateInternalLinks(description, pages.nodes);
    React.useEffect(() => {
        scrollSpy.update();
    }, []);

    return (
        <PrimaryLayout>
            <Container>
                <HeadingContainer>
                    <Heading as="h1" variant="heading10">
                        {name}
                    </Heading>
                    <div dangerouslySetInnerHTML={{ __html: description! }} />
                </HeadingContainer>
                <SeparatorContainer as="div">
                    <Separator orientation="horizontal" verticalSpacing="space100" />
                </SeparatorContainer>
                <ContentContainer>
                    {nodes?.map((node) => (
                        <ClassMembers key={uuid()} {...node} />
                    ))}
                </ContentContainer>
                <RightContainer>
                    <TableOfContents headings={headings} />
                </RightContainer>
            </Container>
        </PrimaryLayout>
    );
};

export default DynamicPage;
